<template>
  <div class="car-item-component">
    <div class="card">
      <div class="card-header bg-default">
        <router-link class="pt-1" :to="{ path: `/fleet/vehicles/${car.uuid}` }">{{ car.CarType.name }} - {{ car.name }} (#{{ car.convadisId }} - {{ car.rego }})</router-link>
        <label class="badge" :class="randomLabel(car.CarType.CarClass.name)">{{ car.CarType.CarClass.name }}</label>
        <label class="badge badge-success" v-if="car.Reservations.length === 0">Available</label>
        <label class="badge badge-danger" v-if="car.Reservations.length > 0">Unavailable</label>
        <label v-if="oneWayTrips" class="badge badge-dark"> <span class="fa fa-long-arrow-alt-right fa-lg"></span> OWT </label>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-5 col-sm-5 col-xs-12">
            <img class="" :src="'https://techcar-dev.s3.amazonaws.com/'+ ( car.CarType.AmazonImage.objectKey ? car.CarType.AmazonImage.objectKey : 'spacer.gif' ) + '?v=' + new Date().getTime()"/>
            <ul class="list-unstyled mt-2">
              <li v-for="feature in car.CarFeatures" :key="feature.uuid">
                <i v-if="feature.icon" class="fa-fw" :class="[
                  feature.icon,
                  {
                    'fab': feature.icon === 'fa-bluetooth',
                    'fa': feature.icon !== 'fa-bluetooth'
                  }
                ]"></i> {{ feature.name }}
              </li>
            </ul>
          </div>
          <div class="col-md-7 col-sm-7 col-xs-12">
            <div class="small" v-if="cost">
              <p class="h5">
                From
                <span class="small">$</span>
                <span class="h4" :class="{ 'text-muted': discount }">
                  <s v-if="discount">{{ cost.preDiscountCost }}</s>
                  <template v-else>{{ cost }}</template>
                </span>
                <span class="h4 text-success" v-if="discount">
                  <strong>{{ cost.subTotalCost }}</strong>
                </span>
              </p>
            </div>
            <template v-if="car.Lot.premiumAmount > 0">
              <p><strong>{{ car.Lot.premiumAmount | to_dollar_amount }} Premium</strong><br>
                {{ car.Lot.premiumReason }}
              </p>
            </template>
            <ul class="ml-0 list-unstyled fa-ul">
              <li class="mb-2"><span class="fa fa-map-marker-alt fa-li"></span>{{ lotInformation.address }}</li>
              <li class="mb-2"><span class="fa fa-map-signs fa-li"></span>{{ lotInformation.addressMemo }}</li>
              <li class="mb-2" v-if="distance"><i class="fas fa-location-arrow fa-li"></i> Approx. {{ distance }} away</li>
            </ul>
          </div>
        </div>
      </div>
      <div v-if="car.Reservations.length > 0" class="alert alert-danger">
        <div v-if="singleConflict" v-html="singleConflict"></div>
        <div v-else>The time you have selected is not available for this car, there are conflicting reservations:
          <template v-for="reservation in car.Reservations">
            <p><strong>{{ reservation.startDate | date_format }} - {{ reservation.endDate | date_format }}</strong></p>
          </template>
        </div>
      </div>
      <div class="card-footer d-flex">
        <span v-if="!car.Reservations.length">
          <button @click="$emit('reserve')" class="btn btn-success btn-sm">
            <span class="fas fa-calendar-check"></span> Reserve
          </button>
        </span>
        <button class="ml-auto btn btn-primary btn-sm" type="button" @click="viewAvailability()">
          <span class="fa fa-calendar-alt"></span>
          View Availability
        </button>
        <a :href="'https://www.google.com/maps?q='+ lotInformation.latitude +','+ lotInformation.longitude" target="_blank" class="btn btn-primary btn-sm ml-1">
          <span class="fa fa-map"></span>
          View on Google Maps
        </a>
      </div>
      <modal title="Car Availability"
        @dismiss="closeCarAvailability()"
        :is-open="isViewingAvailability"
        :dismiss-button="{ label: 'Dismiss' }">
        <car-availability
          :date="start"
          reservation="proposedRes"
          :links="false"
          :availability="carAvailability"
          @previous="previousAvailability()"
          @next="nextAvailability()"
          @change="changeAvailability($event)"
          :car="car">
        </car-availability>
        <div slot="header"></div>
        <div slot="footer">
          <button type="button" class="btn btn-default" @click="closeCarAvailability()">
            Dismiss
          </button>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import CarAvailability from '@/components/admin/CarAvailability'
import OrgLogo from '@/components/OrgLogo'
import Modal from '@/components/Modal'
import Util from '@server/util'
import { randomLabel } from '@/util/helpers'
import {
  OPEN_MODAL,
  CLOSE_MODAL,
  LOAD_CAR_AVAILABILITY
} from '@/vuex/types'
import { mapGetters } from 'vuex'
import moment from 'moment-timezone'
export default {
  name: 'CarItem',
  components: {
    CarAvailability,
    Modal,
    OrgLogo
  },
  props: {
    discount: {
      type: Object
    },
    object: {
      type: Object
    },
    rates: {
      type: Array
    },
    start: {
      type: String
    },
    end: {
      type: String
    },
    driver: {
      type: String
    }
  },
  computed: {
    oneWayTrips () {
      return this.car.oneWayTrips && this.lotInformation.oneWayTrips && this.car.Organization.oneWayTrips && !this.car.LotRelocations.length
    },
    startTime () {
      if (this.start) return moment(this.start).format('x')
    },
    endTime () {
      if (this.end) return moment(this.end).format('x')
    },
    car () {
      return {
        CarType: {
          CarClass: {}
        },
        Organization: false,
        ...this.object
      }
    },
    lotInformation () {
      // default lot
      let lot = this.car.Lot
      let relocationEndTime = false
      if (this.car.LotRelocations) {
        // loop through pending relocations
        for (let relocation of this.car.LotRelocations) {
          // if the relocation is permanent
          if (relocation.permanent) {
            // if the booking is after the relocation
            if (moment(relocation.endDate).isBefore(this.start)) {
              // if there is a previously found relocation and its before the
              // found relocation OR if there is no previously found relocation
              if ((relocationEndTime && moment(relocationEndTime).isBefore(relocation.endDate)) || !relocationEndTime) {
                lot = relocation.Lot
                relocationEndTime = relocation.endDate
              } else {
                lot = relocation.Lot
                relocationEndTime = relocation.endDate
              }
            }
          } else {
            // temporary relocations
            // if the booking times are between the relocation
            if (moment(this.start).isBetween(relocation.startDate, relocation.endDate) &&
              moment(this.end).isBetween(relocation.startDate, relocation.endDate)) {
              lot = relocation.Lot
            }
          }
        }
      }
      return lot
    },
    cost () {
      if (this.rates) {
        let carRate = this.rates.find((el) => {
          return el.CarClassUuid === this.car.CarType.CarClassUuid
        })
        if (carRate && this.start && this.end) {
          let percentDiscount = this.discount ? this.discount.percentDiscount : 0
          let { priceBreakdown } = Util.calculateCosts({
            Organization: this.car.Organization,
            Car: this.car,
            Rate: carRate,
            Reservation: false,
            Lot: this.lotInformation,
            startDate: this.start,
            endDate: this.end,
            extras: [],
            percentDiscount
          })
          if (percentDiscount > 0) {
            return { preDiscountCost: priceBreakdown.preDiscountCost, subTotalCost: priceBreakdown.subTotalCost }
          } else {
            return priceBreakdown.subTotalCost
          }
        }
      }
      return false
    },
    singleConflict () {
      if (this.car.Reservations.length === 1) {
        return `This car is unavailable from <strong>${moment(this.car.Reservations[0].startDate).format('LLLL')}</strong> but will
          be available again at <strong>${moment(this.car.Reservations[0].endDate).format('LLLL')}</strong>`
      } else {
        return false
      }
    },
    distance () {
      if (this.car.distance) {
        if (this.car.distance > 1000) {
          return (this.car.distance / 1000).toFixed(2) + 'km'
        } else {
          return this.car.distance.toFixed(0) + 'm'
        }
      }
      return false
    },
    ...mapGetters([
      'carAvailability'
    ])
  },
  data () {
    return {
      isViewingAvailability: false,
      availabilityDate: false
    }
  },
  methods: {
    randomLabel,
    async viewAvailability () {
      this.isViewingAvailability = true
      this.availabilityDate = moment(this.start).format('x')
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        carUuid: this.car.uuid,
        startTime: this.availabilityDate,
        proposedEndTime: moment(this.end).format('x'),
        proposedStartTime: moment(this.start).format('x')
      })
      this.$store.dispatch(OPEN_MODAL, {
        title: 'Car Availability',
        alreadyOpen: true
      })
    },
    async previousAvailability () {
      this.availabilityDate = moment(this.availabilityDate, 'x').subtract(1, 'days').format('x')
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        carUuid: this.car.uuid,
        startTime: this.availabilityDate,
        proposedEndTime: moment(this.end).format('x'),
        proposedStartTime: moment(this.start).format('x')
      })
    },
    async nextAvailability () {
      this.availabilityDate = moment(this.availabilityDate, 'x').add(1, 'days').format('x')
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        carUuid: this.car.uuid,
        startTime: this.availabilityDate,
        proposedEndTime: moment(this.end).format('x'),
        proposedStartTime: moment(this.start).format('x')
      })
    },
    async changeAvailability (date) {
      this.availabilityDate = moment(date, 'x').format('x')
      await this.$store.dispatch(LOAD_CAR_AVAILABILITY, {
        carUuid: this.car.uuid,
        startTime: this.availabilityDate,
        proposedEndTime: moment(this.end).format('x'),
        proposedStartTime: moment(this.start).format('x')
      })
    },
    closeCarAvailability () {
      this.isViewingAvailability = false
      this.$store.commit(CLOSE_MODAL, 0)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/CarItem.vue